import { useTranslate } from '@im/react-localization';
import Box from '@mui/material/Box';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import DrawerWrapper from 'src/components/Drawer/DrawerWrapper';
import {
  MasterVendorFormData,
  MasterVendorFormKeys,
  useIsValidMasterForm,
  useMasterVendorForm,
  useMasterVendorFormFields,
  usePostCreateMasterVendor,
} from '../../hooks/create-master-vendor';
import {
  FormField,
  costBasisDefault,
} from '../../VendorProgramTemplate/hook/program-template';
import { useUpdateProductSku } from '../../VendorProgramTemplate/hook/program-template-apis';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { useAccount } from '@azure/msal-react';
import useSnackBar from 'src/hooks/useSnackBar';
import { useSearchParams } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import {
  programNameRegex,
  vendorCodeRegex,
} from '../../VendorProgramTemplate/hook/program-template-error-handler';
import { useIsEnvironment } from 'src/hooks/useIsEnvironment';

export default function AddMasterVendor(
  props: Readonly<{ handleClose: () => void }>
) {
  const translate = useTranslate();
  const [searchParams] = useSearchParams();
  const countryCode = searchParams.get('countryCode');
  const companyCode = searchParams.get('companyCode');
  const navigate = useNavigate();
  const MasterVendorFormFields = useMasterVendorFormFields();
  const [formData, setFormData] = useMasterVendorForm();
  const isValidForm = useIsValidMasterForm(formData);
  const [formError, setFormError] = useState<
    { field: string; message: string }[] | undefined
  >(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const user: string = useAccount()?.username ?? '';
  const { showSnackBar } = useSnackBar();
  const updateProductSku = useUpdateProductSku();
  const { isProdEnvironment } = useIsEnvironment();
  const handleFieldChange = (name: string, value: string) => {
    const _formData = { ...formData, [name]: value };
    if (name === 'vendorName' && value !== '' && programNameRegex.exec(value)) {
      setFormError((oldData) => oldData?.filter((data) => data.field !== name));
    }
    if (
      name === 'masterVendorCode' &&
      value !== '' &&
      vendorCodeRegex.exec(value)
    ) {
      setFormError((oldData) => oldData?.filter((data) => data.field !== name));
    }

    setFormData(_formData);
  };

  const createMasterVendor = usePostCreateMasterVendor({
    businessType: formData.businessType ?? '',
    companyCode: formData.companyCode ?? '',
    costBasis: formData.costBasis ?? '',
    createdBy: user,
    discountType: formData.discountType ?? '',
    masterVendorCode: formData.masterVendorCode ?? '',
    source: formData.source ?? '',
    vendorACOPDiscountType: formData.vendorACOPDiscountType ?? '',
    vendorName: formData.vendorName ?? '',
  });
  const handleSave = async () => {
    const invalidField = isValidForm;
    if (invalidField !== true) {
      setFormError(invalidField);
      return;
    }
    setIsSaving(true);

    try {
      const response = await createMasterVendor.mutateAsync();

      if (response.vendorID) {
        // 1. Master Vendor creation success
        showSnackBar({
          autoHide: 3000,
          message: translate('VendorProgram.CreateMasterVendorMessage'),
          severity: 'success',
        });

        const vendorCodes = (formData.masterVendorCode ?? '')
          .split(',')
          .map((code) => code.trim())
          .filter(Boolean);

        if (vendorCodes.length && !isProdEnvironment) {
          updateProductSku.mutate({
            companyCode: companyCode ?? '',
            countryCode: countryCode ?? '',
            vendorCodes,
            vendorName: formData.vendorName ?? '',
          });
        }

        navigate(
          `/vendor-program/program-detail?vendorId=${response.vendorID}&programId=${response.vendorID}&countryCode=${countryCode}&companyCode=${companyCode}`
        );
      }
    } catch (error) {
      handleClose();

      showSnackBar({
        autoHide: 3000,
        message: translate('VendorProgram.CreateMasterVendorMessageError'),
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };
  const disableSave = useMemo<boolean>(() => {
    return [
      'vendorName',
      'masterVendorCode',
      'source',
      'discountType',
      'vendorACOPDiscountType',
      'businessType',
    ].every((k) => Boolean(formData[k as MasterVendorFormKeys]));
  }, [formData]);
  const handleClose = () => {
    setFormData(undefined);
    props.handleClose();
  };
  useEffect(() => {
    setFormData({
      companyCode: companyCode ?? '',
      costBasis: costBasisDefault[countryCode as keyof typeof costBasisDefault],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <DrawerWrapper
      customWidth={600}
      disableSave={!disableSave || isSaving}
      exception={true}
      handleClose={handleClose}
      headerText={translate('VendorProgram.AddNewMasterVendor')}
      loading={isSaving}
      onSaveClick={() => handleSave()}
    >
      <Box>
        <Typography
          sx={(theme) => ({
            color: theme.palette.error.main,
            fontSize: theme.typography.body2.fontSize,
            fontWeight: theme.typography.fontWeightBold,
            lineHeight: theme.typography.body2.lineHeight,
            padding: `${theme.spacing(1.5)} ${theme.spacing(2.5)}`,
          })}
        >
          {translate('Common.AllFieldsRequired')}
        </Typography>
      </Box>
      <Stack
        overflow="auto"
        spacing={1.5}
        sx={(theme) => ({
          padding: `${theme.spacing(1.5)} ${theme.spacing(2.5)}`,
        })}
      >
        {Object.keys(MasterVendorFormFields).map((key) => {
          const fieldValue = formData[key as keyof MasterVendorFormData];
          const field: FormField =
            MasterVendorFormFields[key as MasterVendorFormKeys];
          const _formError = formError?.find(
            (obj) => obj.field === key
          )?.message;
          return (
            <InputFieldContainer
              field={field}
              fieldKey={key}
              fieldValue={fieldValue}
              formError={_formError}
              handleFieldChange={handleFieldChange}
              key={key}
            />
          );
        })}
      </Stack>
    </DrawerWrapper>
  );
}

interface InputFieldContainerProps {
  disabled?: boolean;
  field: FormField;
  fieldKey: string;
  fieldValue: string | undefined;
  formError?: string | boolean | null;
  handleFieldChange: (name: string, value: string) => void;
}
const InputFieldContainer = (props: InputFieldContainerProps) => {
  const translate = useTranslate();
  return (
    <Stack>
      <form>
        <Stack flexDirection={'row'}>
          <Stack direction="row" flex={0.5} minWidth={200}>
            <Typography
              sx={(theme) => ({
                fontSize: theme.typography.body2.fontSize,
              })}
            >
              {props.field.name}
            </Typography>
          </Stack>
          <TextField
            disabled={props.field.disabled}
            error={Boolean(props.formError)}
            fullWidth
            helperText={props.formError}
            id={props.fieldKey}
            inputProps={{ 'data-testid': `input-${props.fieldKey}` }}
            maxRows={4}
            multiline={props.field.multiline}
            name={props.fieldKey}
            onChange={(event) =>
              props.handleFieldChange(props.fieldKey, event.target.value)
            }
            placeholder={translate('Common.TypeValue')}
            select={Boolean(props.field.options?.length)}
            size="small"
            value={props.fieldValue ?? ''}
          >
            {props.field.options?.map((option, i) => (
              <MenuItem key={i} value={String(option.value)}>
                {option.value === '' ? <em>None</em> : option.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </form>
    </Stack>
  );
};
